import { LinkOutlined, HomeOutlined, UserOutlined, TableOutlined, MenuOutlined, KeyOutlined, CrownOutlined, SmileOutlined, SettingOutlined, DashboardOutlined, ControlOutlined, SnippetsOutlined, LockOutlined, CloudUploadOutlined, AppstoreOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  SelectLang,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './services/ant-design-pro/httpRequest';
import { staticRouteMap, fetchRoutesFromApi, filterRoutesByShow } from './config/staticRouteMap';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  menuData?: any[]; // 添加menuData字段存储过滤后的路由配置
  allRoutes?: any[]; // 添加allRoutes字段存储所有路由配置（包括is_show为false的）
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser();
      let currentUser = {
        name: msg.data?.nick || msg.data?.name || '',
        avatar: msg.data?.avatar || '',
        userid: msg.data?.userid || '',
        email: msg.data?.email || '',
        signature: msg.data?.signature || '',
        title: msg.data?.title || '',
        group: msg.data?.group || '',
        access: msg.data?.access || 'visitor',
      };
      return currentUser;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  // 获取路由配置的函数
  const fetchRoutes = async () => {
    try {
      // 从API获取路由配置
      const allRoutes = await fetchRoutesFromApi();
      // 根据is_show属性过滤路由
      const menuData = filterRoutesByShow(allRoutes);
      return { allRoutes, menuData };
    } catch (error) {
      console.error('Failed to fetch routes:', error);
      // 失败时使用静态路由并过滤
      const menuData = filterRoutesByShow(staticRouteMap);
      return { allRoutes: staticRouteMap, menuData };
    }
  };

  // 检查是否有登录token
  const token = window.localStorage.getItem('houseToken');
  const { location } = history;

  // 如果是登录页面，直接返回基础配置
  if ([loginPath, '/user/login'].includes(location.pathname)) {
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
      allRoutes: staticRouteMap, // 使用静态路由作为默认值
      menuData: [], // 登录页面不需要显示菜单
    };
  }

  // 只有在有token的情况下才调用登录相关接口
  if (token) {
    try {
      const currentUser = await fetchUserInfo();
      const { allRoutes, menuData } = await fetchRoutes();
      return {
        fetchUserInfo,
        currentUser,
        settings: defaultSettings as Partial<LayoutSettings>,
        allRoutes,
        menuData,
      };
    } catch (error) {
      console.error('获取用户信息或路由失败:', error);
      // 出错时清除token并重定向到登录页
      window.localStorage.removeItem('houseToken');
      history.push(loginPath);
    }
  }

  // 没有token时返回基础配置
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
    allRoutes: staticRouteMap,
    menuData: [],
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 图标映射表，用于将字符串转换为对应的图标组件
  const iconMap: { [key: string]: React.ReactNode } = {
    'home': <HomeOutlined />,
    'user': <UserOutlined />,
    'table': <TableOutlined />,
    'menu': <MenuOutlined />,
    'key': <KeyOutlined />,
    'crown': <CrownOutlined />,
    'smile': <SmileOutlined />,
    'setting': <SettingOutlined />,
    'link': <LinkOutlined />,
    'dashboard': <DashboardOutlined />,
    'control': <ControlOutlined />,
    'snippets': <SnippetsOutlined />,
    'lock': <LockOutlined />,
    'cloud-upload': <CloudUploadOutlined />,
    'appstore': <AppstoreOutlined />,
  };

  // 处理菜单数据的函数，确保格式正确
  const formatMenuData = (routes: any[]) => {
    console.log('routes', routes);
    return routes.map(route => {
      // 将字符串形式的icon转换为对应的图标组件
      const icon = route.icon && typeof route.icon === 'string'
        ? iconMap[route.icon] || route.icon
        : route.icon;

      const formattedRoute = {
        path: route.path,
        name: route.name,
        icon: icon,
        access: route.access,
        children: route.routes ? formatMenuData(route.routes) : undefined,
      };
      // 移除undefined的属性
      Object.keys(formattedRoute).forEach(key => {
        if (formattedRoute[key] === undefined) {
          delete formattedRoute[key];
        }
      });
      return formattedRoute;
    });
  };

  return {
    actionsRender: () => [
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // 水印
    waterMarkProps: {
      content: false, // initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    // 使用menuDataRender自定义菜单数据，根据is_show过滤后的路由生成菜单
    menuDataRender: () => {
      if (!initialState?.menuData) {
        return [];
      }
      console.log('initialState.menuData', initialState.menuData);
      return formatMenuData(initialState.menuData);
    },
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: false,
    headerTitleRender: (logoDom) => (
      <div style={{
        width: 256,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: '10px 0',
        boxSizing: 'border-box',
        marginLeft: -15,
      }}>
        <img
          src="/logo.png"
          alt="logo"
          style={{ height: 36, width: 'auto', objectFit: 'contain' }}
        />
      </div>
    ),
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
};
